import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSafeSiteId } from '../../state/useActiveSite';
import { fetchSseTransport } from '../fetch-sse';
import { fetchTaskLog } from '../v1-client';
import type { SiteId, TaskLogEntry as V1TaskLogEntry, V1LiveEvent } from '../v1';

export interface TaskLogEntry {
  ts: number;
  event: string;
  detail?: string;
  level?: 'info' | 'warning' | 'error';
}

/** v1 `TaskLogEntry.level` is `'debug'|'info'|'warn'|'error'`; the
 *  timeline UI's CSS modifier classes are `info|warning|error`. */
function adaptLevel(level: string): TaskLogEntry['level'] {
  if (level === 'warn') return 'warning';
  if (level === 'error') return 'error';
  return 'info';
}

/** A v1 log line as the Timeline shows it. The ONE adaptation, for the
 *  REST page and the live line alike, so the two dedupe against each
 *  other on `ts` + `event`. */
function adaptEntry(e: Pick<V1TaskLogEntry, 'at_ms' | 'text' | 'level'>): TaskLogEntry {
  return { ts: e.at_ms, event: e.text, level: adaptLevel(e.level) };
}

/**
 * A live `task_log` payload as a Timeline entry, or null when it is not a
 * log line of THIS task at THIS site.
 *
 * The shape is the worker's (`rmf_bridge.py::_on_task_log_update`), the
 * same `TaskLogEntry` the REST page returns: `{task_id: {site, task},
 * at_ms, level, text, seq}`. Checked the way the api-server's ingestor
 * checks it before storing a line (`history_ingestion.py::
 * _record_log_entry`): no task id, no time or no text, and it is not a
 * log line. The payload's task must agree with the task the key named --
 * the bus authenticates the key, not the payload's fields.
 */
export function adaptLiveTaskLogEntry(
  payload: unknown,
  site: string,
  taskId: string,
): TaskLogEntry | null {
  if (!payload || typeof payload !== 'object' || !site || !taskId) return null;
  const p = payload as Record<string, unknown>;
  const ref = p.task_id as Record<string, unknown> | null | undefined;
  if (!ref || typeof ref !== 'object' || ref.task !== taskId || ref.site !== site) return null;
  if (typeof p.at_ms !== 'number' || !Number.isFinite(p.at_ms) || p.at_ms <= 0) return null;
  if (typeof p.text !== 'string' || p.text === '') return null;
  return adaptEntry({
    at_ms: p.at_ms,
    text: p.text,
    level: (typeof p.level === 'string' ? p.level : 'info') as V1TaskLogEntry['level'],
  });
}

export function useTaskLog(taskId: string | null) {
  const siteId = useSafeSiteId();
  const qc = useQueryClient();
  const queryKey = useMemo(() => ['task-log', siteId, taskId] as const, [siteId, taskId]);

  const query = useQuery<TaskLogEntry[]>({
    queryKey,
    queryFn: async () => {
      if (!taskId || !siteId) return [];
      const page = await fetchTaskLog(siteId as SiteId, { site: siteId as SiteId, task: taskId });
      return page.entries.map(adaptEntry);
    },
    enabled: !!taskId && !!siteId,
  });

  // LIVE APPENDS: this task's new log lines as the worker publishes them
  // (`rmf/v1/sites/{site}/tasks/{task}/log`, classified as `task_log` by
  // `deriveV1LiveEvent`).
  //
  // A KIND CHECK, NOT `subscribe()`. This used `subscribe(taskLogKey(...))`,
  // which does not filter by the key it is handed: every classified event
  // on the site's stream reached it -- robot and fleet states, door and
  // lift states, the 15-second `task_distance` totals -- and any object
  // payload was appended to the open task's Timeline as a blank line. And
  // the log key itself was never classified, so not one real line ever
  // arrived. Matching on the kind and the key's task id, then on the
  // payload's shape, is what makes a stranger's payload unappendable.
  useEffect(() => {
    if (!taskId || !siteId) return;
    return fetchSseTransport().onEvent((evt: V1LiveEvent) => {
      if (evt.kind !== 'task_log' || evt.taskId !== taskId) return;
      if (evt.envelope?.site !== siteId) return;
      const entry = adaptLiveTaskLogEntry(evt.payload, siteId, taskId);
      if (!entry) return;
      qc.setQueryData<TaskLogEntry[]>(queryKey, (prev) => {
        const base = prev ?? [];
        if (base.some((e) => e.ts === entry.ts && e.event === entry.event)) return base;
        return [...base, entry];
      });
    });
  }, [qc, siteId, taskId, queryKey]);

  return query;
}
